import { useState } from 'react';
import { useSelector } from 'react-redux';
import { trackEvent } from 'fathom-client';

import useSolutions from 'lib/hooks/useSolutions';

import { selectCurrentUser } from 'features/auth/slice';

import {
  Button,
  Card,
  flash,
  ImageComparison,
  Loader,
  pluralize,
  recordsMatch,
} from '@frontendmentorio/design-system-v2';

import ProFlag from 'features/shared/ProFlag';

export default function DesignComparison({ challenge, solution }) {
  const currentUser = useSelector(selectCurrentUser);

  const { screenshot } = useSolutions();

  const [isLoading, setIsLoading] = useState(false);
  const isAuthor = recordsMatch(solution.user, currentUser);

  const handleClick = async () => {
    setIsLoading(true);

    try {
      await screenshot(solution);
      trackEvent('Screenshot Created');
    } catch (error) {
      flash.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="flex flex-col gap-5 p-4">
      {(isAuthor || currentUser?.admin) && (
        <div className="flex flex-col gap-4 items-center text-center tablet:flex-row tablet:flex-wrap tablet:justify-between tablet:text-start">
          <div className="flex flex-col gap-1 tablet:min-w-[18rem]">
            <span className="font-semibold">Improved your solution?</span>

            <span className="text-neutral-500">
              Generate a new screenshot to display your changes. You have{' '}
              {!isFinite(+currentUser.credits.screenshots)
                ? 'unlimited screenshots.'
                : `${pluralize(
                    currentUser.credits.screenshots,
                    'screenshot'
                  )} left this month.`}
            </span>

            {!currentUser?.credits.screenshots && (
              <span>
                Get unlimited screenshots as a <ProFlag isLink /> member.
              </span>
            )}
          </div>

          <Button
            className="whitespace-nowrap"
            disabled={isLoading}
            onClick={handleClick}
          >
            {isLoading ? 'Generating...' : 'Regenerate screenshot'}
          </Button>
        </div>
      )}

      {isLoading ? (
        <Loader className="py-12" />
      ) : (
        <ImageComparison
          leftImage={solution.screenshot}
          rightImage={challenge.gallery[0].design}
        />
      )}
    </Card>
  );
}
